import { useCallback, useMemo, useState } from 'react';
import { ObjectsPreviewCard } from 'components/ObjectPreview/ObjectPreviewCard';
import type { TObjectPreviewFilterState } from 'components/ObjectPreview/ObjectPreviewFilter';
import type { ObjectTableProps } from 'components/ObjectTable/ObjectTable';
import { SelectObjectsModal } from 'components/SelectObjectsModal/SelectObjectsModal';
import type { TTEObject, TUserProfile } from '@timeedit/types';
import { Button } from '@timeedit/ui-components';
import { Typography } from '@timeedit/ui-components/antd';
import './UserObjects.scss';

type UserObjectsProps = {
  profile?: Pick<TUserProfile, 'id' | 'userObjects'>;
  onChange: (key: string, value: any) => void;
  /**
   * Disables the Add/Remove buttons only. The Object Type filter and row
   * selection stay interactive — they're read-only view controls.
   */
  disabled?: boolean;
};

export const UserObjects = ({
  profile,
  onChange,
  disabled = false,
}: UserObjectsProps) => {
  const profileId = profile?.id ?? 'default-profile';

  // Local states
  const [objectModalOpen, setObjectModalOpen] = useState<boolean>(false);
  const [selectedObjectsByProfile, setSelectedObjectsByProfile] = useState<
    Record<string, TTEObject[]>
  >({});
  const [extIdToIdMap, setExtIdToIdMap] = useState<Map<string, number>>(
    new Map()
  );
  const [filtersByProfile, setFiltersByProfile] = useState<
    Record<string, TObjectPreviewFilterState>
  >({});

  const selectedObjects = selectedObjectsByProfile[profileId] ?? [];

  const onAddObjects = (selectedObjects: TTEObject[]) => {
    const selectedObjectExtIds = selectedObjects.map(
      (object) => `${object.extId}`
    );
    const uniqueObjects = [...new Set(selectedObjectExtIds)];
    onChange('userObjects', uniqueObjects);
  };

  const onRemoveObjects = useCallback(() => {
    const newObjects = profile?.userObjects.filter(
      (object) =>
        !selectedObjects.some(
          (selectedObject) => selectedObject.extId === object
        )
    );
    onChange('userObjects', newObjects);
    setSelectedObjectsByProfile((prev) => ({ ...prev, [profileId]: [] }));
  }, [selectedObjects, profile?.userObjects, onChange, profileId]);

  const footer: ObjectTableProps['footerContent'] = useMemo(
    () => [
      <Button
        key="add-object"
        type="primary"
        size="small"
        disabled={disabled}
        onClick={() => setObjectModalOpen(true)}
      >
        Add objects
      </Button>,
      <Button
        key="remove-objects"
        type="default"
        size="small"
        disabled={disabled || selectedObjects.length === 0}
        onClick={() => onRemoveObjects()}
      >
        Remove objects
      </Button>,
      <Typography.Text key="selected-count" style={{ marginLeft: 'auto' }}>
        {selectedObjects.length} objects selected
      </Typography.Text>,
    ],
    [selectedObjects, onRemoveObjects, disabled]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedObjects
        .filter(Boolean)
        .map((object) => object.extId),
      onChange: (_, selectedRows) => {
        setSelectedObjectsByProfile((prev) => ({
          ...prev,
          [profileId]: selectedRows,
        }));
      },
      preserveSelectedRowKeys: true,
      hideSelectAll: true,
      columnWidth: 32,
    }),
    [selectedObjects, profileId]
  );

  const queries = useMemo(() => {
    const userObjects = profile?.userObjects;
    return userObjects && userObjects.length > 0 ? { extIds: userObjects } : {};
  }, [profile?.userObjects]);

  const onClose = useCallback(
    (selectedObjects) => {
      if (selectedObjects && Array.isArray(selectedObjects)) {
        onAddObjects(selectedObjects);
      }
      setObjectModalOpen(false);
    },
    [onAddObjects]
  );

  const handleObjectsChange = useCallback((objects: TTEObject[]) => {
    setExtIdToIdMap((prev) => {
      const newMap = new Map(prev);
      objects.forEach((object) => {
        if (object.extId && typeof object.id === 'number') {
          newMap.set(object.extId, object.id);
        }
      });
      return newMap;
    });
  }, []);

  const activeFilter = filtersByProfile[profileId] ?? { typeId: null };

  const handleFilterChange = useCallback(
    (nextFilter: TObjectPreviewFilterState) => {
      setFiltersByProfile((prev) => ({
        ...prev,
        [profileId]: nextFilter,
      }));
    },
    [profileId]
  );

  const selectedObjectIds = useMemo(() => {
    if (!profile?.userObjects || profile.userObjects.length === 0) {
      return [];
    }

    return profile.userObjects
      .map((extId) => extIdToIdMap.get(extId))
      .filter((id): id is number => typeof id === 'number');
  }, [profile?.userObjects, extIdToIdMap]);

  return (
    <div className="user-objects">
      <ObjectsPreviewCard
        showTotalNumber
        title="User Objects"
        queries={queries}
        showLinkToObject={false}
        footer={footer}
        rowSelection={rowSelection}
        virtualOptions={{ enable: false }}
        onObjectsChange={handleObjectsChange}
        filter={activeFilter}
        onFilterChange={handleFilterChange}
      />
      <SelectObjectsModal
        selectedObjectIds={selectedObjectIds}
        open={objectModalOpen}
        onClose={onClose}
        okText="Select"
        width={'70vw'}
      />
    </div>
  );
};
